import api from './axiosConfig';
import { WizardFormData } from 'components/forms/CampaignWizardForm';

export interface HVACampaign {
  id: number;
  bxpid: number;
  name: string;
  serviceid: number;
  campaigntype: number;
  description: string;
  servicename: string;
  brand: string;
  campaigntypedescription: string;
  campaigntypeoutbound: boolean;
  clients: number;
  status: number;
}

export interface HVACampaignWithBXP {
  id: number;
  name: string;
  serviceid: number;
  campaigntype: number;
  description: string;
  servicename: string;
  brand: string;
  campaigntypedescription: string;
  campaigntypeoutbound: boolean;
  bxpid: number;
  bxpname: string;
  callernumber: string;
  timeschedule: number;
  timeschedulename: string;
  callrecording: boolean;
  mediagroup: number;
  mediagroupname: string;
  routingdn: string;
  amd: boolean;
  greeting: number;
  greetingname: string;
  active: boolean;
  inbound_lookup_method: 'DATABASE' | 'API';
}

export interface HVACampaignName {
  id: number;
  bxpcampaignid: number;
  name: string;
}

export interface HVAService {
  id: number;
  name: string;
  brand: string;
  description: string;
}

export interface HVAServiceName {
  id: number;
  name: string;
}

export interface HVACampaignType {
  id: number;
  description: string;
  outbound: boolean;
}

export interface HVANewService {
  name: string;
  brand: string;
  description: string;
}

export interface BXPGreeting {
  id: number;
  shortname: string;
  greetingdesc: string;
  playtype?: string;
  silencedetection?: boolean;
  mrcp_recogopts?: string;
  detection_engine_api?: string;
  active?: boolean;
}

export interface BXPTimeCondition {
  id: number;
  shortname: string;
  startdate: Date | string | null;
  enddate: Date | string | null;
  active: boolean;
}

export interface BXPMediaResourceGroup {
  id: number;
  shortname: string;
  shortdescribe: string;
  rotatemode: string;
  active: boolean;
  crr_time?: Date;
  crr_username?: string;
}

export interface BXPOutboundRule {
  id: number;
  shortname: string;
  trunktype: string;
  trunkprefix: string;
  trunkname: string;
  maxdialresources: number;
  dialtimeout: number;
  canceldialtimeout: number;
  caps_limit: number;
  caps_control_mode: string;
  active: boolean;
}

export interface BXPCallingList {
  id: number;
  shortname: string;
  tablename: string;
  active: boolean;
  crr_username?: string;
  crr_time?: Date;
}

export interface BXPTreatment {
  id: number;
  shortname: string;
  max_attempts_per_record: number;
  daily_attempts_per_record: number;
  next_attempt_after_mins: number;
  next_chain_after_mins: number;
  list_iteration_schema: string;
  next_recall_record_after_days: number;
  maxattempts_per_contact: number;
  daily_attempts_per_contact: number;
  next_recall_contact_after_days: number;
  active: boolean;
}

export interface CustomerData {
  // eslint-disable-next-line
  [key: string]: any;
}

export interface CustomerList {
  record_id: number;
  campaign_id: number;
  blocked: string;
  nombre: string;
  customer_data: CustomerData;
}

export interface BlockCustomerRequest {
  campaign_id: number;
  record_id: number;
  operation: 'block' | 'unblock';
}

export const fetchHVACampaigns = async (): Promise<HVACampaign[]> => {
  try {
    const response = await api.get('hvacampaigns');
    // eslint-disable-next-line
    return response.data.map((hvacampaign: any) => {
      return {
        id: hvacampaign.id,
        bxpid: hvacampaign.bxpcampaignid,
        name: hvacampaign.name,
        serviceid: hvacampaign.serviceid,
        campaigntype: hvacampaign.campaigntype,
        description: hvacampaign.description,
        servicename: hvacampaign.hvaservice.name,
        brand: hvacampaign.hvaservice.brand,
        campaigntypedescription: hvacampaign.hvacampaigntype.description,
        campaigntypeoutbound: hvacampaign.hvacampaigntype.outbound,
        clients: 0,
        status: 0
      };
    });
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

export const fetchHVABXPCampaigns = async (): Promise<HVACampaignWithBXP[]> => {
  try {
    const [hvaResponse, bxpResponse] = await Promise.all([
      api.get('hvacampaigns'),
      api.get('bxpcampaigns')
    ]);

    const hvaCampaigns = hvaResponse.data;
    const bxpCampaigns = bxpResponse.data;

    // eslint-disable-next-line
    return hvaCampaigns.map((hvaCampaign: any) => {
      // eslint-disable-next-line
      const bxpCampaign = bxpCampaigns.find((bxp: any) => bxp.id === hvaCampaign.bxpcampaignid);

      return {
        id: hvaCampaign.id,
        bxpid: hvaCampaign.bxpcampaignid,
        name: hvaCampaign.name,
        serviceid: hvaCampaign.serviceid,
        campaigntype: hvaCampaign.campaigntype,
        description: hvaCampaign.description,
        servicename: hvaCampaign.hvaservice.name,
        brand: hvaCampaign.hvaservice.brand,
        campaigntypedescription: hvaCampaign.hvacampaigntype.description,
        campaigntypeoutbound: hvaCampaign.hvacampaigntype.outbound,
        inbound_lookup_method: hvaCampaign.inbound_lookup_method || 'DATABASE',
        // Campos de BXP
        bxpname: bxpCampaign?.shortname || '',
        callernumber: bxpCampaign?.callernumber || '',
        timeschedule: bxpCampaign?.timeCondition.id || 0,
        timeschedulename: bxpCampaign?.timeCondition.shortname || '',
        callrecording: Boolean(bxpCampaign?.monitor),
        mediagroup: bxpCampaign?.mediaResourceGroup.id || 0,
        mediagroupname: bxpCampaign?.mediaResourceGroup.shortname || '',
        routingdn: bxpCampaign?.routing_dn || '',
        amd: Boolean(bxpCampaign?.amd),
        greeting: bxpCampaign?.greeting.id || 0,
        greetingname: bxpCampaign?.greeting.shortname || '',
        active: Boolean(bxpCampaign?.active)
      };
    });
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// Función para obtener la lista de clientes de una campaña
export const fetchCustomersByCampaign = async (
  campaignId: number
): Promise<CustomerList[]> => {
  try {
    const response = await api.get<CustomerList[]>(
      `/bxpcampaigncustomerlist/${campaignId}`
    );
    return response.data.map(item => ({
      ...item,
      customer_data:
        typeof item.customer_data === 'string'
          ? JSON.parse(item.customer_data)
          : item.customer_data
    }));
  } catch (error) {
    console.error('Error fetching customers list:', error);
    throw error;
  }
};

// Función para bloquear/desbloquear a un cliente
export const blockUnblockCustomer = async ({
  campaign_id,
  record_id,
  operation
}: BlockCustomerRequest): Promise<void> => {
  try {
    await api.patch('/bxpcampaigncustomer/blockunblock', {
      campaign_id,
      record_id,
      operation
    });
  } catch (error) {
    console.error('Error updating customer block status:', error);
    throw error;
  }
};

export const fetchBXPCampaignOutboundRules = async (campaignId: number) => {
  try {
    const response = await api.get(`/bxpcampaignoutrules/${campaignId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign outbound rule associations:', error);
    throw error;
  }
};

export const fetchBXPCampaignCallinglist = async (campaignId: number) => {
  try {
    const response = await api.get(`/bxpcampaigncallinglists/${campaignId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign outbound rule associations:', error);
    throw error;
  }
};

export const fetchHVAUserCampaigns = async (): Promise<HVACampaign[]> => {
  try {
    const response = await api.get('hvausercampaigns');
    // eslint-disable-next-line
    return response.data.map((hvacampaign: any) => {
      return {
        id: hvacampaign.id,
        bxpid: hvacampaign.bxpcampaignid,
        name: hvacampaign.name,
        serviceid: hvacampaign.serviceid,
        campaigntype: hvacampaign.campaigntype,
        description: hvacampaign.description,
        servicename: hvacampaign.hvaservice.name,
        brand: hvacampaign.hvaservice.brand,
        campaigntypedescription: hvacampaign.hvacampaigntype.description,
        campaigntypeoutbound: hvacampaign.hvacampaigntype.outbound,
        clients: hvacampaign.hvacardskpistoday?.clients || 0,
        status: hvacampaign.hvacardskpistoday?.dialingstatus ?? 0
      };
    });
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// La utilizamos en formularios de selección y el backend se encarga de darnos
// sólo las campañas que están asignadas al usuario o todas al administrador
export const fetchHVACampaignNames = async (): Promise<HVACampaignName[]> => {
  try {
    const response = await api.get('hvacampaignnames');
    // eslint-disable-next-line
    return response.data.map((hvacampaignname: any) => {
      return {
        id: hvacampaignname.id,
        name: hvacampaignname.name,
        bxpcampaignid: hvacampaignname.bxpcampaignid
      };
    });
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// La utilizamos en formularios de selección y el backend se encarga de darnos
// sólo las campañas que están asignadas al usuario o todas al administrador
export const fetchHVAOutboundCampaignNames = async (): Promise<
  HVACampaignName[]
> => {
  try {
    const response = await api.get('hvaoutboundcampaignnames');
    // eslint-disable-next-line
    return response.data.map((hvacampaignname: any) => {
      return {
        id: hvacampaignname.id,
        bxpcampaignid: hvacampaignname.bxpcampaignid,
        name: hvacampaignname.name
      };
    });
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// Para los tipos de campaña
export const fetchHVACampaignTypes = async (): Promise<HVACampaignType[]> => {
  try {
    const response = await api.get('hvacampaigntypes');
    // eslint-disable-next-line
    return response.data.map((hvacampaigntype: any) => {
      return {
        id: hvacampaigntype.id,
        description: hvacampaigntype.description,
        outbound: hvacampaigntype.outbound
      };
    });
  } catch (error) {
    console.error('Error fetching campaign types:', error);
    throw error;
  }
};

export const fetchHVACampaignUsers = async (id: number): Promise<number[]> => {
  try {
    const response = await api.get<number[]>(`hvacampaigns/${id}/accounts`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign users:', error);
    throw error;
  }
};

export const fetchHVAUserAssignedCampaigns = async (
  id: number
): Promise<number[]> => {
  try {
    const response = await api.get<number[]>(`hvacampaigns/${id}/campaigns`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user campaigns:', error);
    throw error;
  }
};

export const assignCampaignUsers = async (
  campaignid: number,
  accounts: number[]
): Promise<void> => {
  try {
    await api.post(`hvacampaigns/${campaignid}/assign`, {
      accounts: accounts
    });
  } catch (error) {
    console.error('Error assigning users to campaign:', error);
    throw error;
  }
};

export const assignUserCampaigns = async (
  accountid: number,
  campaigns: number[]
): Promise<void> => {
  try {
    await api.post(`hvacampaigns/${accountid}/assigna`, {
      campaigns: campaigns
    });
  } catch (error) {
    console.error('Error assigning campaigns to user:', error);
    throw error;
  }
};

export const fetchHVAServices = async (): Promise<HVAService[]> => {
  try {
    const response = await api.get('hvaservices');
    // eslint-disable-next-line
    return response.data.map((hvaservice: any) => {
      return {
        id: hvaservice.id,
        name: hvaservice.name,
        brand: hvaservice.brand,
        description: hvaservice.description
      };
    });
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};

// La utilizamos en formularios de selección y el backend se encarga de darnos
// sólo las campañas que están asignadas al usuario o todas al administrador
export const fetchHVAServiceNames = async (): Promise<HVAServiceName[]> => {
  try {
    const response = await api.get('hvaservicenames');
    // eslint-disable-next-line
    return response.data.map((hvaservicename: any) => {
      return {
        id: hvaservicename.id,
        name: hvaservicename.name
      };
    });
  } catch (error) {
    console.error('Error fetching services:', error);
    throw error;
  }
};

export const createHVAService = async (data: HVANewService): Promise<void> => {
  try {
    await api.post(`hvacampaigns/createhvaservice`, data);
  } catch (error) {
    console.error('Error creating service', error);
    throw error;
  }
};

export const deleteHVAService = async (id: number): Promise<void> => {
  try {
    await api.delete(`hvacampaigns/${id}/deletehvaservice`);
    console.log(`Service with id ${id} deleted successfully`);
  } catch (error) {
    console.error(`Error deleting Service with id ${id}:`, error);
    throw error;
  }
};

export const updateHVAService = async (
  id: number,
  data: { name: string; brand: string; description: string }
): Promise<void> => {
  try {
    await api.put(`hvacampaigns/${id}/updatehvaservice`, data);
    // console.log(`Service with id ${id} updated successfully`);
  } catch (error) {
    console.error(`Error updating Service with id ${id}:`, error);
    throw error;
  }
};

// Para la asignación de scorecard a campañas
export const fetchHVACampaignScorecards = async (
  campaignId: number
): Promise<number[]> => {
  try {
    const response = await api.get<number[]>(
      `scorecardmanagement/campaigns/${campaignId}/scorecards`
    );
    return response.data || []; // Asegura que siempre devuelve un array
  } catch (error) {
    console.error('Error fetching campaign scorecards:', error);
    throw error;
  }
};

// Para la asignación de scorecard a campañas
export const assignCampaignScorecards = async (
  campaignId: number,
  scorecardIds: number[]
): Promise<void> => {
  try {
    await api.post(
      `scorecardmanagement/campaigns/${campaignId}/assignscorecards`,
      { scorecardIds: scorecardIds }
    );
  } catch (error) {
    console.error('Error assigning scorecards to campaign:', error);
    throw error;
  }
};

// Para la asignación de campañas a scorecard
export const fetchHVAScorecardCampaigns = async (
  scorecardId: number
): Promise<number[]> => {
  try {
    const response = await api.get<number[]>(
      `scorecardmanagement/scorecards/${scorecardId}/campaigns`
    );
    return response.data || []; // Asegura que siempre devuelve un array
  } catch (error) {
    console.error('Error fetching scorecard campaigns:', error);
    throw error;
  }
};

// Para la asignación de elementos de riesgo a campañas
export const fetchHVACampaignRiskElements = async (
  campaignId: number
): Promise<number[]> => {
  try {
    const response = await api.get<number[]>(
      `riskmanagement/campaign/${campaignId}/elements`
    );
    return response.data || []; // Asegura que siempre devuelve un array
  } catch (error) {
    console.error('Error fetching campaign risk elements:', error);
    throw error;
  }
};

// Para la asignación de elementos de riesgo a campañas
export const assignCampaignRiskElements = async (
  campaignId: number,
  riskelementids: number[]
): Promise<void> => {
  try {
    await api.post(`riskmanagement/campaign/${campaignId}/elements`, {
      riskelementids
    });
  } catch (error) {
    console.error('Error assigning risk elements to campaign:', error);
    throw error;
  }
};

// Para obtener las campañas que tienen un elemento de riesgo específico
export const fetchRiskElementCampaigns = async (
  riskelementid: number
): Promise<number[]> => {
  try {
    const response = await api.get<number[]>(
      `riskmanagement/element/${riskelementid}/campaigns`
    );
    return response.data || [];
  } catch (error) {
    console.error('Error fetching risk element campaigns:', error);
    throw error;
  }
};

// Para asignar un elemento de riesgo a múltiples campañas
export const assignRiskElementCampaigns = async (
  riskelementid: number,
  campaignids: number[]
): Promise<void> => {
  try {
    await api.post(`riskmanagement/element/${riskelementid}/campaigns`, {
      campaignids
    });
  } catch (error) {
    console.error('Error assigning risk element to campaigns:', error);
    throw error;
  }
};

// Para la asignación de campañas a scorecard
export const assignScorecardCampaigns = async (
  scorecardId: number,
  campaignIds: number[]
): Promise<void> => {
  try {
    await api.post(
      `scorecardmanagement/scorecards/${scorecardId}/assigncampaigns`,
      { campaignIds: campaignIds }
    );
  } catch (error) {
    console.error('Error assigning campaigns to scorecard:', error);
    throw error;
  }
};

//***************************************/
// Para el mantenimiento de bxp greetings
export const fetchBXPGreetings = async (): Promise<BXPGreeting[]> => {
  try {
    const response = await api.get<BXPGreeting[]>('/bxpgreetings');
    return response.data;
  } catch (error) {
    console.error('Error fetching BXP greetings:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const createBXPGreeting = async (greetingData: BXPGreeting): Promise<BXPGreeting> => {
  try {
    const response = await api.post<BXPGreeting>(
      '/bxpgreetings/createbxpgreeting',
      greetingData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating BXP greeting:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const updateBXPGreeting = async (id: number, greetingData: Partial<BXPGreeting>): Promise<BXPGreeting> => {
  try {
    // eslint-disable-next-line
    const response = await api.put<BXPGreeting>(`/bxpgreetings/${id}/updatebxpgreeting`, greetingData);
    return response.data;
  } catch (error) {
    console.error('Error updating BXP greeting:', error);
    throw error;
  }
};

export const deleteBXPGreeting = async (id: number): Promise<void> => {
  try {
    await api.delete(`/bxpgreetings/${id}/deletebxpgreeting`);
  } catch (error) {
    console.error('Error deleting BXP greeting:', error);
    throw error;
  }
};

//***************************************/
// Para el mantenimiento de bxp time conditions
export const fetchBXPTimeConditions = async (): Promise<BXPTimeCondition[]> => {
  try {
    const response = await api.get<BXPTimeCondition[]>('/bxptimeconditions');
    return response.data;
  } catch (error) {
    console.error('Error fetching BXP time conditions:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const createBXPTimeCondition = async (timeConditionData: BXPTimeCondition): Promise<BXPTimeCondition> => {
  try {
    const response = await api.post<BXPTimeCondition>(
      '/bxptimeconditions/createbxptimecondition',
      timeConditionData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating BXP time condition:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const updateBXPTimeCondition = async (id: number, timeConditionData: Partial<BXPTimeCondition>): Promise<BXPTimeCondition> => {
  try {
    // eslint-disable-next-line
    const response = await api.put<BXPTimeCondition>(`/bxptimeconditions/${id}/updatebxptimecondition`, timeConditionData);
    return response.data;
  } catch (error) {
    console.error('Error updating BXP time condition:', error);
    throw error;
  }
};

export const deleteBXPTimeCondition = async (id: number): Promise<void> => {
  try {
    await api.delete(`/bxptimeconditions/${id}/deletebxptimecondition`);
  } catch (error) {
    console.error('Error deleting BXP time condition:', error);
    throw error;
  }
};

//***************************************/
// Para el mantenimiento de bxp media resource groups
// eslint-disable-next-line
export const fetchBXPMediaResourceGroups = async (): Promise<BXPMediaResourceGroup[]> => {
  try {
    const response = await api.get<BXPMediaResourceGroup[]>(
      '/bxpmediaresourcegroups'
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching BXP media resource groups:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const createBXPMediaResourceGroup = async (mediaResourceGroupData: BXPMediaResourceGroup): Promise<BXPMediaResourceGroup> => {
  try {
    const response = await api.post<BXPMediaResourceGroup>(
      '/bxpmediaresourcegroups/createbxpmediaresourcegroup',
      mediaResourceGroupData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating BXP media resource group:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const updateBXPMediaResourceGroup = async (id: number, mediaResourceGroupData: Partial<BXPMediaResourceGroup>): Promise<BXPMediaResourceGroup> => {
  try {
    const response = await api.put<BXPMediaResourceGroup>(
      `/bxpmediaresourcegroups/${id}/updatebxpmediaresourcegroup`,
      mediaResourceGroupData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating BXP media resource group:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const deleteBXPMediaResourceGroup = async (id: number): Promise<void> => {
  try {
    await api.delete(
      `/bxpmediaresourcegroups/${id}/deletebxpmediaresourcegroup`
    );
  } catch (error) {
    console.error('Error deleting BXP media resource group:', error);
    throw error;
  }
};

//***************************************/
// Para el mantenimiento de bxp outbound rules
export const fetchBXPOutboundRules = async (): Promise<BXPOutboundRule[]> => {
  try {
    const response = await api.get<BXPOutboundRule[]>('/bxpoutboundrules');
    return response.data;
  } catch (error) {
    console.error('Error fetching BXP outbound rules:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const createBXPOutboundRule = async (outboundRuleData: BXPOutboundRule): Promise<BXPOutboundRule> => {
  try {
    const response = await api.post<BXPOutboundRule>(
      '/bxpoutboundrules/createbxpoutboundrule',
      outboundRuleData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating BXP outbound rule:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const updateBXPOutboundRule = async (id: number, outboundRuleData: Partial<BXPOutboundRule>): Promise<BXPOutboundRule> => {
  try {
    const response = await api.put<BXPOutboundRule>(
      `/bxpoutboundrules/${id}/updatebxpoutboundrule`,
      outboundRuleData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating BXP outbound rule:', error);
    throw error;
  }
};

export const deleteBXPOutboundRule = async (id: number): Promise<void> => {
  try {
    await api.delete(`/bxpoutboundrules/${id}/deletebxpoutboundrule`);
  } catch (error) {
    console.error('Error deleting BXP outbound rule:', error);
    throw error;
  }
};

//***************************************/
// Para el mantenimiento de bxp calling lists
export const fetchBXPCallingLists = async (): Promise<BXPCallingList[]> => {
  try {
    const response = await api.get<BXPCallingList[]>('/bxpcallinglists');
    return response.data;
  } catch (error) {
    console.error('Error fetching BXP calling lists:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const createBXPCallingList = async (callingListData: BXPCallingList): Promise<BXPCallingList> => {
  try {
    const response = await api.post<BXPCallingList>(
      '/bxpcallinglists/createbxpcallinglist',
      callingListData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating BXP calling list:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const updateBXPCallingList = async (id: number, callingListData: Partial<BXPCallingList>): Promise<BXPCallingList> => {
  try {
    const response = await api.put<BXPCallingList>(
      `/bxpcallinglists/${id}/updatebxpcallinglist`,
      callingListData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating BXP calling list:', error);
    throw error;
  }
};

export const deleteBXPCallingList = async (id: number): Promise<void> => {
  try {
    await api.delete(`/bxpcallinglists/${id}/deletebxpcallinglist`);
  } catch (error) {
    console.error('Error deleting BXP calling list:', error);
    throw error;
  }
};

//***************************************/
// Para el mantenimiento de bxp treatments
export const fetchBXPTreatments = async (): Promise<BXPTreatment[]> => {
  try {
    const response = await api.get<BXPTreatment[]>('/bxptreatments');
    return response.data;
  } catch (error) {
    console.error('Error fetching BXP treatments:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const createBXPTreatment = async (treatmentData: BXPTreatment): Promise<BXPTreatment> => {
  try {
    const response = await api.post<BXPTreatment>(
      '/bxptreatments/createbxptreatment',
      treatmentData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating BXP treatment:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const updateBXPTreatment = async (id: number, treatmentData: Partial<BXPTreatment>): Promise<BXPTreatment> => {
  try {
    const response = await api.put<BXPTreatment>(
      `/bxptreatments/${id}/updatebxptreatment`,
      treatmentData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating BXP treatment:', error);
    throw error;
  }
};

export const deleteBXPTreatment = async (id: number): Promise<void> => {
  try {
    await api.delete(`/bxptreatments/${id}/deletebxptreatment`);
  } catch (error) {
    console.error('Error deleting BXP treatment:', error);
    throw error;
  }
};

//***************************************/
// Para el mantenimiento de bxp/hva campaigns
// eslint-disable-next-line
export const createHVABXPCampaign = async (campaignData: WizardFormData): Promise<WizardFormData> => {
  try {
    const response = await api.post<WizardFormData>(
      '/hvabxpcampaigns/create',
      campaignData
    );
    return response.data;
  } catch (error) {
    console.error('Error creating HVA BXP campaign:', error);
    throw error;
  }
};

// eslint-disable-next-line
export const updateHVABXPCampaign = async (id: number, campaignData: WizardFormData): Promise<WizardFormData> => {
  try {
    const response = await api.put<WizardFormData>(
      `/hvabxpcampaigns/${id}/update`,
      campaignData
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating HVA BXP campaign with id ${id}:`, error);
    throw error;
  }
};

export const deleteHVABXPCampaign = async (id: number): Promise<void> => {
  try {
    await api.delete(`/hvabxpcampaigns/${id}/delete`);
  } catch (error) {
    console.error(`Error deleting HVA BXP campaign with id ${id}:`, error);
    throw error;
  }
};
